import { createSelector } from 'reselect';
import { buildRoundDates, roundLabelWithDate } from '../utils/round_label_utils';
import { SUMMARY_KEYS, isPurseSummary } from '../actions/summaries_index';
import { SITE_CONTEXTS } from '../constants';
import { withoutPurseColumns } from '../utils/column_utils';

export const selectIsManagerSite = (state) =>
  state.leaderboardV2.context === SITE_CONTEXTS.MANAGER_SITE;

// The purse tiles follow the league's hide-purse option the way the purse
// column does (selectHidesPurse below): the flag rides on every tournaments
// list response, so a toggle reaches pollers without a reload. Rails already
// leaves them out of displaySummaries when the option is on at page load;
// this is the poll-time layer.
const isSummaryVisible = (displaySummaries, hidesPurse, key) =>
  Boolean(displaySummaries[key]) && !(hidesPurse && isPurseSummary(key));

export const selectEnabledSummaryKeys = createSelector(
  (state) => state.leaderboardV2.displaySummaries,
  (state) => selectHidesPurse(state),
  (displaySummaries = {}, hidesPurse) =>
    SUMMARY_KEYS.filter((key) => isSummaryVisible(displaySummaries, hidesPurse, key))
);

// A summary is open while its slice entry exists; the first one in display
// order owns the mobile/landscape screen (and the modal highlight). A purse
// entry that outlives a hide-purse toggle (closePurseSummaries racing its
// own fetch) is open for no consumer.
export const selectOpenSummaryKey = (state) => {
  const hidesPurse = selectHidesPurse(state);
  return SUMMARY_KEYS.find((key) => state.summaries[key] && !(hidesPurse && isPurseSummary(key))) ?? null;
};

export const selectAllEvents = createSelector(
  (state) => state.leaderboardV2.eventsList,
  (eventsList) => Object.values(eventsList).flat()
);

// Memoized id → event index covering live event ids and their snapshot ids.
// selectParentEvent runs per connected row on every store update (event rows,
// header rows, open tables), so the lookup must be O(1): the index rebuilds
// only when the events list itself is replaced (FETCH_EVENTS_SUCCESS /
// COMMIT_EVENTS), not on every dispatch.
const selectEventsById = createSelector(selectAllEvents, (events) => {
  const byId = new Map();
  events.forEach((event) => {
    byId.set(event.id, event);
    event.snapshots?.forEach((snapshot) => byId.set(snapshot.id, event));
  });
  return byId;
});

export const selectParentEvent = (state, eventId) =>
  selectEventsById(state).get(String(eventId));

export const selectRoundDates = createSelector(
  (state) => state.leaderboardV2.roundList,
  buildRoundDates
);

// The committed round for the selected tile's header on mobile/landscape, so
// the viewer sees which round is on screen without opening the picker - on
// every page, single-round ones included. Null only when the committed round
// is not in the list. A string, so the connected tile's prop comparison holds
// between dispatches.
export const selectCurrentRoundLabel = (state) => {
  const { roundList = [], roundId } = state.leaderboardV2;
  const round = roundList.find((r) => String(r.id) === String(roundId));
  return round ? roundLabelWithDate(round, selectRoundDates(state)) : null;
};

export const selectRoundOrderMap = createSelector(
  (state) => state.leaderboardV2.roundList,
  (roundList = []) =>
    roundList.reduce((acc, r, i) => {
      acc[String(r.id)] = r.index ?? i;
      return acc;
    }, {})
);

export const selectHasExpandableScorecards = (state, eventId) =>
  selectParentEvent(state, eventId)?.hasExpandableScorecards ?? false;

// Cached per scopes object: aggregate_row_container runs this once per row, and
// the answer only changes when the scopes themselves do.
const flagPresence = new WeakMap()

const hasFlagData = (scopes, key) => {
  let byKey = flagPresence.get(scopes)

  if (!byKey) {
    byKey = {}
    flagPresence.set(scopes, byKey)
  }

  if (byKey[key] === undefined) {
    byKey[key] = Object.values(scopes)
      .some((aggregates) => (aggregates ?? []).some((agg) => (agg?.[key] ?? []).length > 0))
  }

  return byKey[key]
}

// The setting only says which flag to show. A field where nobody has one would
// otherwise reserve an empty column - and shift the sticky offsets with it.
export const selectDisplayedFlags = (state, eventId) => {
  const mode = state.leaderboardV2.displayedFlags

  if (!mode) {
    return null
  }

  const scopes = state.event?.[String(eventId)]?.data?.scopes

  if (!scopes) {
    return null
  }

  return hasFlagData(scopes, mode === 'country_flags' ? 'country' : 'stateIsos') ? mode : null
}

// State code -> flag image URL, from the initial state (state flags only).
export const selectStateFlagUrls = (state) => state.leaderboardV2.stateFlagUrls ?? {}

// Manager-only action buttons (tournaments API `cut_line_button` /
// `points_and_purse_button`). Both resolve to a props object or null when the
// button must not render. The cut line button asks `checkUrl` at click time
// whether the toggle needs confirmation (see cut_line_button.jsx); the
// points/purse button is a full-page redirect to the backend toggle action,
// exactly like the old HTML leaderboard.
export const selectCutLineButton = (state, eventId) => {
  if (!selectIsManagerSite(state)) return null;

  const event = selectParentEvent(state, eventId);
  const button = event?.cutLineButton;
  if (button?.state !== 'displayed' || !button.cutListId) return null;

  const { cutLineButtonUrl, roundId } = state.leaderboardV2;
  if (!cutLineButtonUrl) return null;

  return {
    shown: button.shown,
    checkUrl: cutLineButtonUrl
      .replace(':event_id', event.id)
      .replace(':round_id', roundId)
      .replace(':cut_list_id', button.cutListId),
  };
};

// The button renders only when clickable, and 'displayed' is the API's
// clickability contract — every other state (or its absence) renders nothing.
const isClickablePointsAndPurseButton = (button) => button?.state === 'displayed';

// The points/purse toggle is spec-wide: grouped tournaments carry the button
// on their spec row, and only when every event in the group is toggleable —
// the button either navigates or does not render at all; a stand-alone event
// shows its own button.
export const selectPointsAndPurseButton = (state, id, { isSpec = false, isGroup = false } = {}) => {
  if (!selectIsManagerSite(state)) return null;

  let button;
  let specId;
  let specType;
  if (isSpec) {
    const events = state.leaderboardV2.eventsList[id] ?? [];
    if (!events.length || !events.every((e) => isClickablePointsAndPurseButton(e?.pointsAndPurseButton))) {
      return null;
    }
    button = events[0].pointsAndPurseButton;
    specId = id;
    specType = events[0]?.tournamentSpecType;
  } else {
    if (isGroup) return null;
    const parentEvent = selectParentEvent(state, id);
    button = parentEvent?.pointsAndPurseButton;
    specId = parentEvent?.tournamentSpecId;
    specType = parentEvent?.tournamentSpecType;
  }

  if (!isClickablePointsAndPurseButton(button) || !specId) return null;

  const { pointsAndPurseUrl, pointsAndPurseLegacyUrl, roundId } = state.leaderboardV2;
  const template = specType === 'TournamentSpec' ? pointsAndPurseLegacyUrl : pointsAndPurseUrl;
  if (!template) return null;

  return {
    shown: button.shown,
    href: template.replace(':spec_id', specId).replace(':round_id', roundId),
  };
};

// The "Resolve Ties" cut-line button (tournament results API
// `resolve_ties_button`, per result scope): shown when the cut settled away
// from its target position because of an unresolved tie. Navigates to the
// adjust page, like the old HTML leaderboard. The flag is baked into the
// cached results JSON for all viewers, so the manager-site gate lives here.
// The button data lives in the displayed event's slice (a round snapshot when
// one is selected), but the link embeds the live (parent) event id — the
// adjust page only lists live events, and its auto-open matches on their ids.
export const selectResolveTiesHref = (state, eventId, scopeId) => {
  if (!selectIsManagerSite(state)) return null;

  const button = state.event[String(eventId)]?.data?.resolveTiesButton?.[scopeId];
  if (button?.state !== 'displayed') return null;

  const { resolveTiesUrl, roundId } = state.leaderboardV2;
  if (!resolveTiesUrl) return null;

  const parentEventId = selectParentEvent(state, eventId)?.id ?? String(eventId);

  return resolveTiesUrl
    .replace(':round_id', roundId)
    .replace(':event_id', parentEventId);
};

// The league's "hide purse from public leaderboard displays" option as the
// tournaments list reports it (hide_purse, portal requests only). The manager
// site always sees the purse - the API sends false there anyway; the guard
// spells the contract out.
export const selectHidesPurse = (state) =>
  state.leaderboardV2.hidePurse === true && !selectIsManagerSite(state);

// Cached per columns array: the row containers ask once per row on every
// store update, and connect shallow-compares the result.
const purseFreeColumnsByColumns = new WeakMap();

const purseFreeColumns = (columns) => {
  let filtered = purseFreeColumnsByColumns.get(columns);

  if (!filtered) {
    filtered = withoutPurseColumns(columns);
    purseFreeColumnsByColumns.set(columns, filtered);
  }

  return filtered;
};

// The event's columns as this viewer may see them. The cached results payload
// always carries the purse columns (a toggle never regenerates it, as in
// legacy); hiding is applied here, one layer above, so every consumer -
// header, rows, the mobile expand gate - agrees.
export const selectVisibleColumns = (state, eventId) => {
  const columns = state.event[eventId]?.data?.columns ?? [];
  return selectHidesPurse(state) ? purseFreeColumns(columns) : columns;
};

// The mobile table's sticky left group: every cell before the first data cell.
const MOBILE_LEFT_GROUP_COLUMNS = new Set(['position', 'name', 'affiliation']);

const hasScoreColumn = (columns) => columns.some((column) => column.id === 'score');

// The collapsed mobile table shows two data columns, or three while the round
// is live: a Thru column is on the table - the single round's own, or an
// in-progress round's Thru/To Par pair. leaderboard_v2_mobile.sass keys the
// third slot on the `current-thru` marker header_row puts on both. On the
// narrowest phones (useNarrowMobile) the row has room for two whatever the
// round's state; the stylesheet's narrow rules ignore the marker there.
const collapsedMobileDataSlots = (columns, rounds, narrowMobile) => {
  if (narrowMobile) return 2;

  const hasLiveThru =
    columns.some((column) => column.id === 'thru')
    || (hasScoreColumn(columns) && Object.values(rounds).some((round) => round.inProgress));

  return hasLiveThru ? 3 : 2;
};

// Data cells rendered after the name: the remaining columns, plus - on a
// horizontal leaderboard, where aggregate_row renders them off the score
// column - one total per round and the Thru/To Par pair of each round still
// in progress.
const countMobileDataCells = (columns, rounds) => {
  const baseCount = columns.filter((column) => !MOBILE_LEFT_GROUP_COLUMNS.has(column.id)).length;

  if (!hasScoreColumn(columns)) return baseCount;

  return baseCount + Object.values(rounds).reduce(
    (sum, round) => sum + 1 + (round.inProgress ? 2 : 0),
    0
  );
};

// Expand/Collapse View has a job only when the collapsed table hides a column.
export const selectShowMobileExpandToggle = (state, eventId, narrowMobile = false) => {
  const data = state.event[eventId]?.data;
  if (!data) return false;

  const columns = selectVisibleColumns(state, eventId);
  const rounds = data.rounds ?? {};

  return countMobileDataCells(columns, rounds) > collapsedMobileDataSlots(columns, rounds, narrowMobile);
};
